import {ChangeEvent, Dispatch, FocusEvent, SetStateAction, useCallback, useEffect, useState} from 'react';
import {FlatpickrFunctions} from './use-flatpickr';

type UseDateInputProps = {
    value?: string | number | Date;
    formatDate: FlatpickrFunctions['formatDate'];
    parseDate: FlatpickrFunctions['parseDate'];
};

type UseDateInputReturn = {
    inputValue: string;
    setInputValue: Dispatch<SetStateAction<string>>;
    /**
     * handles the blur of the input, setting the input value
     * to either a formatted version of the date that was entered,
     * or a formatted version of the last valid date that was entered.
     */
    handleBlur: (e: FocusEvent<HTMLInputElement>) => void;
    /**
     * Only changes the input value to what was typed by the user. We can't
     * trigger changes to the datepicker (like via setDate) here, because it can cause
     * the input value to get clobbered.
     */
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

export function useDateInput(props: UseDateInputProps): UseDateInputReturn {
    const {
        value,
        formatDate,
        parseDate
    } = props;
    const [inputValue, setInputValue] = useState('');
    const getDate = useCallback((item?: Date | string | number): Date | undefined => {
        if (typeof item === 'number') {
            return new Date(item);
        } else if (typeof item === 'string') {
            return parseDate(item);
        }
        return item;
    }, [parseDate]);
    const handleBlur = useCallback((e: FocusEvent<HTMLInputElement>): void => {
        const maybeDate = parseDate(e.target.value);

        if (maybeDate) {
            setInputValue(formatDate(maybeDate));
        }
    }, [parseDate, formatDate]);
    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
        setInputValue(e.target.value);
    }, []);

    useEffect(() => {
        const dateValue = getDate(value);
        const stringValue = dateValue ? formatDate(dateValue) : '';

        setInputValue(stringValue);
    }, [value, formatDate, getDate]);

    return {
        inputValue,
        setInputValue,
        handleBlur,
        handleChange
    };
}
